<script setup>
import { onMounted, onUnmounted, computed, watch } from "vue";
const SideMenu = defineAsyncComponent(() => import("./SideMenu.vue"));
const VideoMenu = defineAsyncComponent(() => import("./VideoMenu.vue"));
import NavBar from "./_nav.vue";
import Bottom from "./_footer.vue";
import ConfirmDialog from "primevue/confirmdialog";
import { UserStore } from "@/front/stores/user_store.js";
const userStore = UserStore();
import { VideoStore } from "@/front/stores/video_store.js";
const videoStore = VideoStore();
import { useRouter } from "vue-router";
const router = useRouter();
const route = useRoute();
import { useLocalization } from "@/front/composables/StringComposer";
import DeactivatedDialog from "@/front/components/DeactivatedDialog.vue";
const VideoSavingAnimation = defineAsyncComponent(() =>
  import("@/front/components/VideoSavingAnimation.vue")
);
// Initialize localization once during setup
useLocalization();

const { t } = useI18n();

const showSideMenu = computed(() => {
  return route.meta?.menu && userStore.present;
});

window.trackEvent = (event, properties) => {
  window.loadAnalytics().then(() => {
    if (window.analytics) {
      window.analytics.track(event, properties);
      log.info(5, "📊 CIO event tracked");
    }
  });
};

window.identifyUser = () => {
  window.loadAnalytics().then(() => {
    if (window.analytics) {
      window.analytics.identify(userStore.user.id, {
        email: userStore.user.email,
        full_name: userStore.user.full_name,
        first_name: userStore.user.first,
        last_name: userStore.user.last,
        plan: userStore.organization.plan,
        subscribed: userStore.user.subscribed,
      });
      log.info(5, "📊 CIO user identified");
    }
  });
};

window.trackPage = () => {
  window.loadAnalytics().then(() => {
    if (window.analytics) {
      window.analytics.page();
      log.info(5, "📊 CIO page tracked");
    }
  });
};

// ✅ Returns a Promise so .then() works
window.loadAnalytics = () => {
  if (!userStore.present || window.analytics || !window.VITE_CIO_SITE) return Promise.resolve(); // nothing to load
  log.info(5, "Load analytics with site key:", window.VITE_CIO_SITE)

  // Prevent duplicate loading
  if (window._analyticsLoading) return window._analyticsLoading;

  window._analyticsLoading = (async () => {
    const [{ default: Analytics }, { default: customerIOPlugin }] =
      await Promise.all([import("analytics"), import("@analytics/customerio")]);

    window.analytics = Analytics({
      app: "mindstamp",
      plugins: [
        customerIOPlugin({
          siteId: window.VITE_CIO_SITE,
        }),
      ],
    });
    log.info(5, "📊 CIO Analytics loaded");
    window.analytics.page();
  })();

  return window._analyticsLoading;
};

onMounted(() => {
  window.addEventListener("popstate", handlePopState);
  window.loadAnalytics().then(() => {
    if (window.analytics) {
      window.identifyUser();
      window.analytics.page();
    }
  });
});

onUnmounted(() => {
  window.removeEventListener("popstate", handlePopState);
});

const handlePopState = (event) => {
  log.info(5, "🔄 Main handlePopState", event);
  window.location.reload();
};

watch(
  () => route.meta,
  (newMeta) => {
    if (newMeta) {
      if (newMeta.auth && !userStore.present) {
        let str = "/login";
        let next = route.path + route.search;
        if (next != "/") {
          str += "?return=" + next;
        }
        router.push(str);
      }
    }
  },
  { immediate: true }
);
const isPlayback = computed(() => {
  if (!route.name) {
    return true;
  }
  let result = ["playback_path", "embed_path"].includes(route.name);
  return result;
});
</script>

<template>
  <main class="bg-black" :class="{ 'bg-gray-200': !isPlayback }">
    <Toast :life="2000" position="bottom-center" />
    <ConfirmDialog />
    <div class="flex">
      <keep-alive v-if="showSideMenu">
        <div
          v-if="$route.meta?.menu === 'SideMenu'"
          class="w-[200px] bg-gray-900"
        >
          <SideMenu />
        </div>
        <div
          v-else-if="$route.meta?.menu === 'VideoMenu'"
          class="w-[200px] bg-gray-900"
        >
          <VideoMenu />
        </div>
      </keep-alive>
      <div
        :class="{
          'flex-1': showSideMenu,
          'w-full': !showSideMenu,
          'bg-black': ['playback_path', 'embed_path'].includes(
            $route.meta?.name
          ),
        }"
        class="h-screen overflow-scroll no-scrollbar"
      >
        <router-view v-slot="{ Component }">
          <!-- <transition name="fade" mode="in" style="--animate-duration: 0.1s;"> -->
          <component :is="Component" />
          <transition name="fade">
            <VideoSavingAnimation v-if="videoStore.showSavingAnimation" />
          </transition>
          <!-- </transition> -->
        </router-view>
      </div>
    </div>
  </main>
  <bottom />
</template>